import styled, { css } from 'styled-components';

import { ButtonForIconFav } from 'components/favorite-icon/styled';
import { IconLoyaltyPoints } from 'components/icons/loyalty-points-icon';
import { Picture } from 'components/picture';
import { ProgressMeter } from 'components/progress-meter';
import { HIGHLIGHT_COLOR } from 'pages/loyalty/loyalty-incentives-components/constants';
import { RewardLockIcon } from 'pages/loyalty/loyalty-incentives-components/reward-lock-icon';
import { StyledBlockRenderer } from 'pages/loyalty/loyalty-offers/loyalty-offers.styled';
import { RewardLock } from 'pages/loyalty/reward-lock';
import { UpcomingOfferBadge } from 'pages/loyalty/upcoming-offer/upcoming-offer-badge';
import { primitive } from 'styles/constants/primitives';
import { fadeIn } from 'utils/style/animations';

export const RewardText = styled.h3`
  font-family: ${Styles.fontFamily.body};
  font-weight: ${Styles.fontWeight.normal};
  letter-spacing: normal;
  line-height: 1rem;
  margin: 0;
  text-align: start;

  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 0.75rem;
  height: 2.9375rem;

  ${Styles.desktop} {
    font-size: 0.875rem;
    height: 3.125rem;
  }
`;

export const PriceTextContainer = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  position: absolute;
  inset-block-end: 12px;
`;

export const SlashedPriceText = styled.span`
  color: ${primitive.$disabledText};
  font-family: ${Styles.fontFamily.body};
  font-weight: 400;
  text-decoration: line-through;
  font-size: 0.75rem;
  line-height: 1rem;

  ${Styles.desktop} {
    font-size: 0.875rem;
    line-height: 1rem;
  }
`;

export const DiscountedPriceText = styled(SlashedPriceText)`
  font-family: ${Styles.fontFamily.brand};
  color: #387f49;
  text-decoration: none;
  position: relative;
  inset-block-start: 0.0625rem;
`;

export const BackgroundImage = styled(Picture)`
  width: 100%;
  height: 100%;
  position: absolute;
`;

export const DefaultBackground = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  background: ${Styles.color.background};
`;

export const OfferImageWrapper = styled.div<{ isMobile: boolean }>`
  position: relative;
  width: 100%;
  min-height: 5.9375rem;
  z-index: ${Styles.zIndex.below};
  align-self: center;
  border-radius: 0.5rem;
  object-fit: cover;
  aspectratio: ${p => (p.isMobile ? '142/95' : '148/99')};

  ${Styles.desktop} {
    min-height: 6.1875rem;
  }
`;

export const StyledProgressMeter = styled(ProgressMeter)`
  border: 0;
  background: white;
  width: 95%;
  margin: 0.25rem 0;
  ${Styles.desktop} {
    width: 80%;
  }
`;

export const StyledRewardPoints = styled.p`
  font-size: 1rem;
  margin-block-start: 0.25rem;
  margin-block-end: 0;
  margin-inline: 0;
  text-transform: lowercase;
  strong {
    font-weight: ${Styles.fontWeight.heavy};
    color: ${Styles.color.primary};
  }
`;

export const OfferCardContainer = styled.li`
  height: 100%;
  width: 100%;
  padding-inline: 0;
  background: ${primitive.$white};
  border-radius: 0.75rem;
  border: 2px solid #0000001a;
`;

export const ClickContainer = styled.button<{
  isQuestChallengeIcentive?: boolean;
  shouldShowPrice?: boolean;
}>`
  background: ${primitive.$white};
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 0.75rem;
  padding-block-start: 12px;
  padding-inline: 12px;
  gap: 0.5rem;
  border: 0;
  opacity: 0;
  animation: ${fadeIn} 0.4s forwards;
  cursor: pointer;
  width: 100%;
  height: 100%;
  margin-inline: auto;
  padding-block-end: ${p => (p.shouldShowPrice ? '36px' : '12px')};

  ${OfferImageWrapper} {
    img {
      ${p =>
        p.isQuestChallengeIcentive &&
        css`
          opacity: 0.5;
        `}
    }
  }
`;

export const LabelAndFavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 1.5rem;
`;

export const LabelWrapper = styled.div``;

export const Overlay = styled.div`
  opacity: 0.275;
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: ${Styles.zIndex.normal};
  background-color: ${Styles.color.grey.three};
`;

export const StyledLockIcon = styled(RewardLockIcon)`
  & > div {
    inset-block-start: 3px;
    transform: scale(0.4);
  }
`;

export const PointTextContainer = styled.div`
  display: flex;
  align-items: center;
  line-height: 1.25;
`;

export const StyledPointsIcon = styled(IconLoyaltyPoints)`
  margin-inline-end: 0.25em;
`;

export const HighlightedPointsIcon = styled(StyledPointsIcon)`
  path {
    fill: ${HIGHLIGHT_COLOR};
  }
`;

export const StyledUnLockedRewardPoints = styled.div`
  display: none;
`;

export const StyledRewardLock = styled(RewardLock)`
  border-radius: 0.5rem;

  > div {
    background-color: ${Styles.color.tertiary};
    ::before,
    ::after {
      border-color: ${Styles.color.tertiary};
    }
    ::after {
      background-color: inherit;
    }
  }
`;

export const PointsNeededLockIcon = styled(StyledRewardLock)`
  transform: scale(0.4);
  position: relative;
  width: 1.2rem;
  height: 1.2rem;
  margin-block: 0;
  margin-inline-start: 0;
  margin-inline-end: 0.25rem;
  display: none;
  > div {
    background-color: ${HIGHLIGHT_COLOR};
    ::before,
    ::after {
      border-color: ${HIGHLIGHT_COLOR};
    }
    ::after {
      background-color: inherit;
    }
  }
`;

export const IncentiveCardDescription = styled(StyledBlockRenderer)`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  max-height: 2rem;
`;

export const LabelBadge = styled.div`
  display: flex;
  background: #0000000a;
  border-radius: 0.25rem;

  gap: 0.25rem;
  padding-inline: 0.5rem;
  padding-block: 0.25rem;
  height: 100%;
  align-items: center;
  max-width: 7rem;

  & > * {
    flex-shrink: 0;
  }
`;

export const LabelText = styled.span`
  font-family: ${Styles.fontFamily.body};
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 400;
  letter-spacing: normal;
  text-align: start;
  white-space: nowrap;

  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const WrapperButton = styled.div`
  border-radius: 50%;
`;

export const LoadingWrapper = styled.div`
  height: 2.125rem;
`;

export const StyledUpcomingOfferBadge = styled(UpcomingOfferBadge)`
  margin-block-start: 1rem;
`;

export const FavButtonWrapper = styled(ButtonForIconFav)`
  height: 1.5rem;
  width: 1.5rem;

  &: hover {
    background: none;
  }

  &: active {
    box-shadow: none;
  }
`;
